import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "../../components/page-layout";
import useBaseRequest from "../../api/BaseRequest";
import MetaDataGateway from "../../api/gateways/MetaDataGateway";
import { IMetaDataItem } from "../../entities/IMetaData";
import { Language } from "../../components/translations/language";
import { useToast } from "../../components/toast";
import { Icon } from "../../modules/icon";

export const DocumentFiltersScreen = () => {
  const { t, i18n } = useTranslation();
  const { success, danger } = useToast();
  const [items, setItems] = useState<IMetaDataItem[]>([]);

  const { execute: fetchItems, data: metaDataItems, loading } = useBaseRequest(
    MetaDataGateway.getAllMetaDataItems
  );

  const { execute: saveItems, loading: saving } = useBaseRequest(
    MetaDataGateway.updateEnableForFiltering,
    {
      onCompleted: () => success(t("FILTERS.DOCUMENT_FILTERS.SAVED")),
      onError: () => danger(t("FILTERS.DOCUMENT_FILTERS.SAVE_ERROR")),
    }
  );

  useEffect(() => {
    fetchItems(undefined);
  }, []);

  useEffect(() => {
    if (!metaDataItems) return;
    const sorted = [...metaDataItems].sort(
      (a, b) => new Date((b as any).createdAt).getTime() - new Date((a as any).createdAt).getTime()
    );
    setItems(sorted);
  }, [metaDataItems]);

  const getLabel = (item: IMetaDataItem): string =>
    item.translations?.[i18n.language as Language] ||
    item.translations?.["en" as Language] ||
    item.name;

  const handleToggle = (id: number) => {
    setItems((prev) =>
      prev.map((item) =>
        (item as any).id === id ? { ...item, enableForFiltering: !item.enableForFiltering } : item
      )
    );
  };

  const handleSave = () => {
    const payload = items.map((item) => ({
      id: (item as any).id,
      enableForFiltering: item.enableForFiltering !== false,
    }));
    saveItems(payload);
  };

  return (
    <PageLayout
      title={t("FILTERS.DOCUMENT_FILTERS.TITLE")}
      loading={loading}
      badge={<Icon type="fa-solid fa-circle-info" size={16} title="FILTERS.DOCUMENT_FILTERS.INFO" />}
    >
      <table className="table table-hover" style={{ width: "auto" }}>
        <thead>
          <tr>
            <th>{t("FILTERS.DOCUMENT_FILTERS.FIELD_NAME")}</th>
            <th style={{ width: 120, textAlign: "center" }}>{t("FILTERS.DOCUMENT_FILTERS.ENABLED")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={(item as any).id}>
              <td>{getLabel(item)}</td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={item.enableForFiltering !== false}
                  onChange={() => handleToggle((item as any).id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t("SAVING") : t("SAVE")}
        </button>
      </div>
    </PageLayout>
  );
};
